import { Injectable, Signal, WritableSignal, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslocoService } from '@jsverse/transloco';
import { firstValueFrom, skip } from 'rxjs';
import { LocationsService } from './api/generated/locations/locations.service';
import { GetLocationsInUseParams, LocationOption } from './api/generated/model';
import { SelectOption } from '../types/entity-definition.interface';
import { MeasureType } from '../types/table.interface';
import { buildLocationFilterOptions, UsedLocation } from '../utils/location-filter';

/** Which entities' locations to surface. Several screens (a grid + an overlaid dialog) can be
 *  active at once, so each context keeps its own option set. */
export type LocationFacetContext = 'manual' | 'automatic' | 'analysisPost' | 'auscultation' | 'allMeasures';

interface ContextState {
  readonly source: WritableSignal<UsedLocation[]>;
  readonly options: Signal<SelectOption[]>;
  activeAssetId: number | null;
}

@Injectable({ providedIn: 'root' })
export class UsedLocationOptionsService {
  private readonly api = inject(LocationsService);
  private readonly translate = inject(TranslocoService);
  private readonly states = new Map<LocationFacetContext, ContextState>();

  constructor() {
    this.translate.langChanges$.pipe(skip(1), takeUntilDestroyed()).subscribe(() => {
      for (const [context, state] of this.states) {
        if (state.activeAssetId !== null) void this.fetch(state.activeAssetId, context, state);
      }
    });
  }

  options(context: LocationFacetContext): Signal<SelectOption[]> {
    return this.state(context).options;
  }

  load(assetId: number | null | undefined, context: LocationFacetContext): void {
    const id = assetId ?? null;
    const state = this.state(context);
    if (id === state.activeAssetId) return;
    state.activeAssetId = id;
    if (id !== null) void this.fetch(id, context, state);
    else state.source.set([]);
  }

  private state(context: LocationFacetContext): ContextState {
    let state = this.states.get(context);
    if (!state) {
      const source = signal<UsedLocation[]>([]);
      const options = computed(() =>
        buildLocationFilterOptions(source(), this.translate.translate('common.dialogs.selectMeasures.filters.noLocation')),
      );
      state = { source, options, activeAssetId: null };
      this.states.set(context, state);
    }
    return state;
  }

  private async fetch(assetId: number, context: LocationFacetContext, state: ContextState): Promise<void> {
    try {
      const locations = await firstValueFrom(this.api.getLocationsInUse<LocationOption[]>(this.paramsFor(assetId, context)));
      state.source.set(locations.map(location => ({ id: location.id, name: location.name })));
    } catch {
      state.activeAssetId = null;
      state.source.set([]);
    }
  }

  private paramsFor(assetId: number, context: LocationFacetContext): GetLocationsInUseParams {
    switch (context) {
      case 'manual':
        return { assetId, type: MeasureType.MANUAL };
      case 'automatic':
        return { assetId, type: MeasureType.AUTOMATIC };
      case 'analysisPost':
        return { assetId, type: MeasureType.ANALYSIS_POST };
      case 'auscultation':
        return { assetId, source: 'info' };
      case 'allMeasures':
        return { assetId };
    }
  }
}
